'use client';

import {
  type Parser,
  type UseQueryStateOptions,
  type Values,
  parseAsArrayOf,
  parseAsString,
  useQueryStates,
} from 'nuqs';
import { useMemo } from 'react';
import type { FilterOptions } from './product-listing-filter-sheet';

export type FilterValuesType = Values<
  Record<string, Parser<string> | Parser<string[]>>
>;

export function useProductListingFilters({
  filterOptions,
  history = 'replace',
  scroll = false,
  shallow = false,
  throttleMs = 0,
  debounceMs = 0,
  clearOnDefault = false,
  startTransition,
}: {
  filterOptions: FilterOptions[];
  history?: 'replace' | 'push';
  scroll?: boolean;
  shallow?: boolean;
  throttleMs?: number;
  debounceMs?: number;
  clearOnDefault?: boolean;
  startTransition: (fn: () => void) => void;
}) {
  const queryStateOptions = useMemo<
    Omit<UseQueryStateOptions<string>, 'parse'>
  >(() => {
    return {
      history,
      scroll,
      shallow,
      throttleMs,
      debounceMs,
      clearOnDefault,
      startTransition,
    };
  }, [
    history,
    scroll,
    shallow,
    throttleMs,
    debounceMs,
    clearOnDefault,
    startTransition,
  ]);

  const filterParsers = useMemo(() => {
    return filterOptions.reduce<
      Record<string, Parser<string> | Parser<string[]>>
    >((acc, field) => {
      if (
        field.type === 'color' ||
        field.type === 'finish' ||
        field.type === 'pattern'
      ) {
        // Multi-select filter
        acc[field.type] = parseAsArrayOf(parseAsString, ',')
          .withOptions({
            ...queryStateOptions,
            clearOnDefault: true,
          })
          .withDefault([]);
      } else if (field.type === 'price') {
        // Price range filter - handle min and max separately
        acc.priceMin = parseAsString
          .withOptions({
            ...queryStateOptions,
            clearOnDefault: true,
          })
          .withDefault('');
        acc.priceMax = parseAsString
          .withOptions({
            ...queryStateOptions,
            clearOnDefault: true,
          })
          .withDefault('');
      }
      return acc;
    }, {});
  }, [queryStateOptions, filterOptions]);

  const [filterValues, setFilterValues] = useQueryStates(filterParsers, {
    shallow: false,
  });

  return { filterValues, setFilterValues };
}
